import { Link, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { pageTrail } from "@/lib/pageTrail";
import { usePageTrailNameValue, usePageTrailVia } from "@/components/PageTrail";
import { TOUCH_TARGET } from "@/components/buttonStyles";

// The page's breadcrumb, at the top bar's left (BRK-S269; docs/navigation.md).
// It is the portal's one trail: no page header draws its own
// (test/lib/page-header-conformance.test.ts). Nav, 2026-09-28: "we need to use
// the navbar and make it standard for the navigation and breadcrumbs".
//
// From md it reads in full, "Accounts / Cedarline Contracting LLC". A phone
// has room for the way back alone, "‹ Accounts", and the page's own title is
// the first thing under the top bar anyway. A top-level destination shows its
// name at every width.
export function TopBarTrail() {
  const { pathname } = useLocation();
  const trail = pageTrail(pathname, usePageTrailNameValue(), usePageTrailVia());
  if (!trail) return null;

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex min-w-0 items-center gap-2 text-body">
        {/* A page's own ancestry beyond its parent (BRK-S399) reads from md;
            a phone shows the nearest way back alone. */}
        {trail.ancestors?.map((a) => (
          <li key={a.to} className="hidden shrink-0 items-center gap-2 md:flex">
            <Link
              to={a.to}
              className={`rounded text-brk-dim transition-colors hover:text-brk-text ${TOUCH_TARGET}`}
            >
              {a.label}
            </Link>
            <span aria-hidden="true" className="text-brk-dim">
              /
            </span>
          </li>
        ))}
        {trail.parent && (
          <li
            className={`flex items-center gap-2 ${
              // A record parent (an insured's name, BRK-S399) can be long, so
              // it truncates rather than pushing the top bar sideways; a
              // sidebar list's short label keeps its width.
              trail.ancestors ? "min-w-0" : "shrink-0"
            }`}
          >
            <Link
              to={trail.parent.to}
              className={`inline-flex min-w-0 items-center gap-0.5 rounded text-brk-dim transition-colors hover:text-brk-text ${TOUCH_TARGET}`}
            >
              <ChevronLeft
                size={18}
                aria-hidden
                className="-ml-1 shrink-0 md:hidden"
              />
              <span className="truncate">{trail.parent.label}</span>
            </Link>
            <span aria-hidden="true" className="hidden text-brk-dim md:inline">
              /
            </span>
          </li>
        )}
        <li
          aria-current="page"
          className={`min-w-0 truncate font-medium text-brk-text ${
            trail.parent ? "hidden md:block" : ""
          }`}
        >
          {trail.current}
        </li>
      </ol>
    </nav>
  );
}
